/** Strategy library (V3Strategies): templates and mine, spot/futures filter. */
import { useState } from "react";
import { Shell } from "../components/Shell.tsx";
import { Empty, ErrorNote, Loading, Page, Segmented } from "../components/ui.tsx";
import { api } from "../lib/api.ts";
import { useApi } from "../lib/data.ts";
import { describeAny, marketWord, tradeStyle } from "../lib/describe.ts";
const STYLE = { signals: "signals", weights: "weights", grid: "grid" } as const;
import { Link, navigate } from "../lib/router.tsx";
import type { StrategyRow } from "../lib/types.ts";

export async function startBacktest(versionId: string, from = Date.UTC(2020, 5, 1), capital = 10_000) {
  const r = await api<{ id: string }>("/backtests", { method: "POST", body: { versionId, from, capital } });
  navigate(`/backtests/${r.id}`);
}

export function Strategies() {
  const { data, loading } = useApi<{ strategies: StrategyRow[] }>("/strategies");
  const [kind, setKind] = useState<"template" | "user">("template"), [mkt, setMkt] = useState<"all" | "spot" | "futures">("all");
  const [err, setErr] = useState<Error | null>(null), [busy, setBusy] = useState<string | null>(null);
  const list = (data?.strategies ?? []).filter((s) => s.kind === kind && (mkt === "all" || (mkt === "spot" ? s.market === "spot" : s.market !== "spot")));
  return (
    <Shell crumb="Strategies · Library" actions={<Link to="/strategies/new" className="btn-primary no-underline hover:no-underline">New strategy</Link>}>
      <Page eyebrow="Strategies" title="Library" width={1100}>
        <div className="flex flex-wrap items-center gap-3">
          <Segmented label="Whose" value={kind} onChange={setKind} options={[["template", "Templates"], ["user", "Mine"]]} />
          <Segmented label="Market" value={mkt} onChange={setMkt} options={[["all", "All"], ["spot", "Spot"], ["futures", "Futures"]]} />
        </div>
        <ErrorNote error={err} />
        {loading ? <Loading /> : !list.length ? <Empty title={kind === "user" ? "No strategies of your own yet" : "No templates match"}>{kind === "user" && <>Tell Dukun how you want to trade: <Link to="/strategies/new">write a strategy</Link>.</>}</Empty> : (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(320px,100%),1fr))] gap-4">
            {list.map((s) => (
              <article key={s.version_id} className="card flex flex-col gap-3 p-5">
                <div className="flex items-start gap-2"><h2 className="m-0 flex-1 text-[17px] font-semibold text-ink-strong">{s.name}</h2><span className="chip">{marketWord(s.market)}{tradeStyle(s.spec) ? ` · ${STYLE[tradeStyle(s.spec)!.by]} · ${tradeStyle(s.spec)!.timeframe}` : ""}</span></div>
                <ul className="m-0 flex list-disc flex-col gap-1 pl-5 text-[14px] leading-relaxed text-ink/85">{describeAny(s.spec).slice(0, 3).map((l) => <li key={l}>{l}</li>)}</ul>
                <div className="mt-auto flex flex-wrap gap-2 pt-1">
                  <button className="btn" disabled={busy === s.version_id} onClick={async () => { setBusy(s.version_id); setErr(null); try { await startBacktest(s.version_id); } catch (e) { setErr(e as Error); } finally { setBusy(null); } }}>{busy === s.version_id ? "Queuing…" : "Backtest"}</button>
                  <button className="btn" onClick={() => navigate(`/bots/new?strategy=${encodeURIComponent(s.version_id)}`)}>Start a bot</button>
                  <button className="btn" onClick={() => navigate(`/strategies/new?from=${encodeURIComponent(s.version_id)}`)}>{s.kind === "user" ? "Change with Dukun" : "Customize with Dukun"}</button>
                </div>
              </article>
            ))}
          </div>
        )}
      </Page>
    </Shell>
  );
}
